// nav/useOverlayNav.js: Phase 3 #5b: Preact binding over overlay-nav.js.
//
// Holds the pure NavState in component state and wires the browser
// history so the overlay behaves like a real screen for the Back button
// WITHOUT being a route:
//   • open  → pushState a marker entry (so Back has something to pop)
//   • Back / popstate landing off the marker → close the overlay
//   • close button → history.back() (which fires popstate → close)
//
// All DOM/history side effects live here; the transition logic stays in
// the pure module (unit-tested). This hook is intentionally thin so the
// e2e spec (how-to-play.spec.js) is what proves the wired behavior.

import { useState, useCallback, useEffect, useRef } from 'preact/hooks';
import {
  initialNavState,
  openHowToPlay,
  closeOverlay,
  isOverlayOpen,
  HISTORY_MARKER,
  isOverlayMarkerState,
} from './overlay-nav.js';

export function useOverlayNav() {
  const [nav, setNav] = useState(initialNavState);
  // Tracks whether WE pushed the marker entry, so close() only calls
  // history.back() when there's a marker to pop (avoids walking off the
  // app's own history when the overlay was closed some other way).
  const pushedRef = useRef(false);

  const open = useCallback(() => {
    setNav((s) => openHowToPlay(s));
    if (typeof window !== 'undefined' && !pushedRef.current) {
      pushedRef.current = true;
      window.history.pushState({ [HISTORY_MARKER]: 'how-to-play' }, '');
    }
  }, []);

  // Close the overlay state directly (used by the popstate handler, which
  // has ALREADY popped the marker entry, so we must not call back again).
  const closeFromPop = useCallback(() => {
    pushedRef.current = false;
    setNav((s) => closeOverlay(s));
  }, []);

  // Close from a UI control: step back through history so the URL/state
  // returns to the pre-open entry. The popstate listener then clears nav.
  const close = useCallback(() => {
    if (typeof window !== 'undefined' && pushedRef.current) {
      window.history.back();
    } else {
      closeFromPop();
    }
  }, [closeFromPop]);

  // Browser Back / forward: if we land somewhere that is NOT our marker
  // entry while the overlay is open, the user backed out, close it.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onPop = (e) => {
      if (!isOverlayMarkerState(e.state)) closeFromPop();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [closeFromPop]);

  return { nav, open, close, isOpen: isOverlayOpen(nav) };
}
